import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSession } from '@/hooks/useSession';
import { ApiError } from '@/lib/api';
import { listDomains, type SendingDomain } from '@/lib/domains';
import { sendEmail, type SendResult } from '@/lib/sends';
import { UpgradeModal } from './UpgradeModal';

const EMAIL_RE = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
const LOCALPART_RE = /^[a-z0-9._%+-]+$/i;

export interface SendModalDraft {
  id: string;
  currentVersion: number;
  subject: string | null;
  fromEmail: string | null;
  fromName: string | null;
}

export interface SendModalProps {
  draft: SendModalDraft;
  userEmail: string;
  /**
   * Called right before submit so the editor can save the current canvas as a
   * new version (otherwise we'd send the last-saved DB version). Returns the
   * version that should be used for sending, or null to fall back to draft.currentVersion.
   */
  onBeforeSend?: () => Promise<number | null>;
  onClose: () => void;
  onSent: (result: SendResult) => void;
}

export function SendModal({ draft, userEmail, onBeforeSend, onClose, onSent }: SendModalProps) {
  const { user } = useSession();
  const [domains, setDomains] = useState<SendingDomain[] | null>(null);
  const [domainsError, setDomainsError] = useState<string | null>(null);

  // Recipient input (textarea: comma or newline separated)
  const [toInput, setToInput] = useState('');

  // Split from-email into local-part + domain dropdown.
  // Default local-part: from draft.fromEmail if it has a local part, else "hello".
  const initialLocalPart = useMemo(() => {
    if (draft.fromEmail && draft.fromEmail.includes('@')) {
      return draft.fromEmail.split('@')[0] || 'hello';
    }
    return 'hello';
  }, [draft.fromEmail]);

  const initialDomainHint = useMemo(() => {
    if (draft.fromEmail && draft.fromEmail.includes('@')) {
      return draft.fromEmail.split('@')[1] || '';
    }
    return '';
  }, [draft.fromEmail]);

  const [localPart, setLocalPart] = useState(initialLocalPart);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [fromName, setFromName] = useState(draft.fromName ?? '');
  const [subject, setSubject] = useState(draft.subject ?? '');
  const [replyTo, setReplyTo] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<{ used: number; limit: number; bonus: number } | null>(
    null,
  );
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const all = await listDomains();
        const verified = all.filter((d) => d.status === 'verified');
        setDomains(verified);
        // Pick a sensible default for the domain dropdown
        if (verified.length > 0) {
          const match = verified.find((d) => d.domain === initialDomainHint);
          setSelectedDomain(match ? match.domain : verified[0].domain);
        }
      } catch (e) {
        setDomainsError(e instanceof Error ? e.message : 'Failed to load verified domains');
      }
    })();
  }, [initialDomainHint]);

  // Parse comma/newline separated recipients into an array.
  const recipients = useMemo(() => {
    const parts = toInput
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return parts;
  }, [toInput]);

  const recipientErrors = useMemo(() => {
    return recipients.filter((r) => !EMAIL_RE.test(r));
  }, [recipients]);

  const fromEmail = selectedDomain ? `${localPart.trim()}@${selectedDomain}` : '';

  const canSubmit =
    !submitting &&
    !!selectedDomain &&
    !!localPart.trim() &&
    LOCALPART_RE.test(localPart.trim()) &&
    recipients.length > 0 &&
    recipientErrors.length === 0 &&
    (!replyTo.trim() || EMAIL_RE.test(replyTo.trim()));

  function handleTestToMe() {
    setToInput(userEmail);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setQuotaInfo(null);

    try {
      // Auto-save current editor canvas first (if caller supports it).
      let version: number | undefined;
      if (onBeforeSend) {
        const v = await onBeforeSend();
        if (v) version = v;
      }

      const result = await sendEmail({
        draft_id: draft.id,
        version,
        to: recipients,
        from_email: fromEmail,
        from_name: fromName.trim() || null,
        reply_to: replyTo.trim() || null,
        subject: subject.trim() || undefined,
      });
      onSent(result);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        if (e.status === 429 && e.payload && typeof e.payload === 'object') {
          const payload = e.payload as { quota?: { used: number; limit: number; bonus: number } };
          if (payload.quota) setQuotaInfo(payload.quota);
          setUpgradeOpen(true);
        }
      } else {
        setError(e instanceof Error ? e.message : 'Send failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Dialog open onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="gap-6 overflow-y-auto rounded-2xl sm:max-w-lg">
          <form onSubmit={handleSubmit}>
            <DialogHeader className="text-left">
              <DialogTitle>Send email</DialogTitle>
              <DialogDescription>
                Sends what's currently in the editor. The recipient sees this exact HTML.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-1 pb-6">
              {domainsError ? (
                <Alert variant="destructive" className="mt-2">
                  <AlertDescription>{domainsError}</AlertDescription>
                </Alert>
              ) : null}

              {domains !== null && domains.length === 0 ? (
                <div className="border-warn text-warn mt-5 rounded-lg border bg-warn/5 p-4 text-sm dark:bg-warn/10">
                  <p className="font-medium text-foreground">You need a verified sending domain first.</p>
                  <p className="text-muted-foreground mt-1">
                    We can't send from arbitrary addresses — your domain needs DKIM/SPF set up so
                    inboxes accept the mail.
                  </p>
                  <Link to="/app/settings" className="text-primary mt-3 inline-block text-sm font-medium hover:underline">
                    Set up a domain →
                  </Link>
                </div>
              ) : null}

              <Field label="To" hint="One per line, or comma-separated. Up to 50.">
                <Textarea
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  placeholder="alice@example.com"
                  rows={2}
                  disabled={submitting}
                  className="font-mono text-sm resize-none"
                />
                <div className="text-muted-foreground mt-1.5 flex items-center justify-between text-xs">
                  <span>
                    {recipients.length > 0
                      ? `${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`
                      : 'No recipients yet'}
                    {recipientErrors.length > 0 ? (
                      <span className="text-destructive ml-2">· {recipientErrors.length} invalid</span>
                    ) : null}
                  </span>
                  <Button
                    type="button"
                    variant="link"
                    className="text-primary h-auto p-0 text-xs"
                    onClick={handleTestToMe}
                    disabled={submitting}
                  >
                    Send test to me ({userEmail})
                  </Button>
                </div>
              </Field>

              <Field label="From">
                <div className="focus-within:ring-ring/50 bg-background flex items-center gap-0 overflow-hidden rounded-md border shadow-xs focus-within:ring-[3px]">
                  <Input
                    type="text"
                    value={localPart}
                    onChange={(e) => setLocalPart(e.target.value)}
                    disabled={submitting || !selectedDomain}
                    placeholder="hello"
                    className="rounded-none border-0 shadow-none focus-visible:ring-0 md:text-sm flex-1 min-w-0 font-mono"
                  />
                  <span className="text-muted-foreground px-1">@</span>
                  <select
                    value={selectedDomain}
                    onChange={(e) => setSelectedDomain(e.target.value)}
                    disabled={submitting || !domains || domains.length === 0}
                    className="bg-transparent pr-3 py-2 font-mono text-sm focus:outline-none"
                  >
                    {domains === null ? (
                      <option value="">Loading…</option>
                    ) : domains.length === 0 ? (
                      <option value="">(no verified domain)</option>
                    ) : (
                      domains.map((d) => (
                        <option key={d.id} value={d.domain}>
                          {d.domain}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                {localPart.trim() && !LOCALPART_RE.test(localPart.trim()) ? (
                  <span className="text-destructive mt-1 block text-xs">
                    Only letters, digits, ._%+- are allowed.
                  </span>
                ) : null}
              </Field>

              <Field label="From name" hint="Shown next to your address in the recipient's inbox.">
                <Input
                  type="text"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="e.g. My Company"
                  disabled={submitting}
                />
              </Field>

              <Field label="Subject">
                <Input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={draft.subject ?? '(no subject)'}
                  disabled={submitting}
                />
              </Field>

              <Field label="Reply-to" hint="Optional. Replies go here instead of the From address.">
                <Input
                  type="email"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  placeholder="support@yourcompany.com"
                  disabled={submitting}
                />
                {replyTo.trim() && !EMAIL_RE.test(replyTo.trim()) ? (
                  <span className="text-destructive mt-1 block text-xs">Not a valid email.</span>
                ) : null}
              </Field>

              {error ? (
                <Alert variant="destructive" className="mt-2">
                  <AlertDescription>
                    {error}
                    {quotaInfo ? (
                      <>
                        <div className="mt-2 text-xs opacity-90">
                          Used {quotaInfo.used} / {quotaInfo.limit + quotaInfo.bonus} sends this month.
                        </div>
                        <Button
                          type="button"
                          variant="link"
                          className="text-primary h-auto p-0 text-xs mt-2"
                          onClick={() => setUpgradeOpen(true)}
                        >
                          Upgrade to Pro →
                        </Button>
                      </>
                    ) : null}
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="flex items-center justify-end gap-2 pt-6">
                <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSubmit}>
                  {submitting ? 'Sending…' : `Send to ${recipients.length || 0} →`}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <UpgradeModal
        open={upgradeOpen}
        currentTier={user?.tier ?? 'free'}
        context="You've hit your monthly sends limit. Upgrade to lift the cap immediately."
        onClose={() => setUpgradeOpen(false)}
      />
    </>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-5 space-y-2">
      <div>
        <Label>{label}</Label>
        {hint ? <p className="text-muted-foreground mt-1 text-xs">{hint}</p> : null}
      </div>
      {children}
    </div>
  );
}
